'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { MenuAccordionItem } from './MenuAccordionItem';
import { MenuSubPanel } from './MenuSubPanel';
import { Icon } from '@/components/Icon';
import type { EventTrackingResolved } from '@/lib/gtm/pushCmsEventTracking';
import type { MenuItem } from '@/types/menu';

interface SectionMenuProps {
  items: MenuItem[];
  menuTracking?: EventTrackingResolved[];
  phone?: string;
  matelsoPhoneLabel?: string;
  isOpen: boolean;
  onClose: () => void;
}

export const SectionMenu = ({
  items,
  menuTracking,
  phone,
  matelsoPhoneLabel,
  isOpen,
  onClose,
}: SectionMenuProps) => {
  const t = useTranslations();
  const [openItem, setOpenItem] = useState<MenuItem | null>(null);

  /*
   * Rendered whether or not it is open, and hidden with the `hidden` attribute
   * instead (SH-528). Returning `null` when closed kept the whole link list out
   * of the server HTML, so a crawler saw no internal links from the header at
   * all — this site uses the burger menu at every breakpoint, so there is no
   * second, always-visible nav to fall back on.
   *
   * `hidden` and not an opacity/height trick: it takes the subtree out of the
   * accessibility tree *and* out of the tab order, so a closed menu is not
   * something a keyboard user tabs through invisibly.
   */

  const handleClose = () => {
    setOpenItem(null);
    onClose();
  };

  const toggle = (item: MenuItem) =>
    setOpenItem((prev) => (prev?.label === item.label ? null : item));

  return (
    <nav
      id="section-navigation"
      aria-label={t('navigation.section')}
      hidden={!isOpen}
      aria-hidden={!isOpen}
      className="bg-secondary-lighter text-foreground relative z-10 lg:bg-white"
    >
      <div
        className={classNames(
          'mx-auto max-w-6xl px-0 py-2 md:px-4 lg:px-9',
          openItem ? 'lg:py-8' : 'lg:py-6',
        )}
      >
        {openItem && (
          <div className="hidden lg:block">
            <MenuSubPanel
              item={openItem}
              menuTracking={menuTracking}
              onBack={() => setOpenItem(null)}
              onClose={handleClose}
            />
          </div>
        )}
        <ul
          className={classNames(
            '[&>li]:border-card-border mb-auto grid list-none gap-x-8 sm:grid-cols-2 lg:grid-cols-3 [&>li]:border-b sm:[&>li]:border-t sm:[&>li]:border-b-0 [&>li:last-child]:border-b-0 sm:[&>li:nth-last-child(-n+2)]:border-b lg:[&>li:nth-last-child(-n+3)]:border-b',
            openItem && 'lg:hidden',
          )}
        >
          {items.map((item) => {
            const isExpanded = openItem?.label === item.label;
            return (
              <li
                key={`${item.label}-${item.linkTarget}`}
                className={isExpanded ? 'sm:col-span-full' : ''}
              >
                <MenuAccordionItem
                  item={item}
                  menuTracking={menuTracking}
                  isExpanded={isExpanded}
                  onToggle={() => toggle(item)}
                  onClose={handleClose}
                />
              </li>
            );
          })}
        </ul>
      </div>

      <div className="bg-white px-4 py-5 text-center lg:hidden">
        <p className="mb-1 text-sm">{t('header.advisoryText')}</p>
        {phone && (
          <a
            href={`tel:${phone.replace(/[^+\d]/g, '')}`}
            className="text-primary inline-flex items-center gap-2 text-lg font-bold"
          >
            <Icon src="/icons/svg/phone-filled.svg" size={20} />
            {matelsoPhoneLabel || phone}
          </a>
        )}
      </div>
    </nav>
  );
};
